import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, LinearProgress } from "@material-ui/core";
import { PasswordTextField, PlainTextField } from "components";
import i18n from "../i18n";

// prop types
interface ForgotPasswordDialogProps {
    error: string | null,
    isLoading: boolean,
    showDialog: boolean,
    onClose: () => void,
    onCompleteForgotPassword: (email: string, code: string, password: string) => void,
    onForgotPassword: (email: string) => void,
}

// forgot password dialog
const ForgotPasswordDialog = ({ error, isLoading, showDialog, onClose, onCompleteForgotPassword, onForgotPassword }: ForgotPasswordDialogProps) => {
    // ui state
    const [emailAddress, setEmailAddress] = useState("");
    const [code, setCode] = useState("");
    const [codeSent, setCodeSent] = useState(false);
    const [password, setPassword] = useState("");

    // did hide hook -> clear input fields
    useEffect(() => {
        if (!showDialog) {
            setEmailAddress("");
            setCode("");
            setCodeSent(false);
            setPassword("");
        }
    }, [showDialog]);

    // execute action
    const execute = async () => {
        if (codeSent) {
            onCompleteForgotPassword(emailAddress, code, password);
        } else {
            await onForgotPassword(emailAddress);
            setCodeSent(true);
        }
    };

    // relevant errors
    const invalidMail = ["UserNotFoundException", "userDoesNotExist"];

    // render
    return (
        <Dialog
            open={showDialog}
            onClose={onClose}
        >
            <DialogTitle>{i18n.forgotPassword}</DialogTitle>
            <DialogContent dividers>

                {/* description text */}
                <DialogContentText>
                    {codeSent ? i18n.forgotPasswordCodeSentDesc : i18n.forgotPasswordDesc}
                </DialogContentText>

                {/* email */}
                <PlainTextField
                    autoFocus
                    disabled={isLoading || codeSent}
                    error={invalidMail.includes(error!)}
                    helperText={(invalidMail.includes(error!)) && i18n.errors[error!]}
                    label={i18n.emailAddress}
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                />

                {/* code */}
                {codeSent && (
                    <PlainTextField
                        autoFocus
                        disabled={isLoading}
                        error={error === "CodeMismatchException"}
                        helperText={(error === "CodeMismatchException") && i18n.errors[error!]}
                        label={i18n.code}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                    />
                )}

                {/* new password */}
                {codeSent && (
                    <PasswordTextField
                        disabled={isLoading}
                        error={error === "passwordPolicy"}
                        helperText={(error === "passwordPolicy") && i18n.errors[error!]}
                        label={i18n.newPassword}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                )}

            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" onClick={execute} disabled={isLoading}>
                    {i18n.ok}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ForgotPasswordDialog;
